import _ from 'lodash'
import { useTheme } from "@emotion/react"
import { L1ReasonableButton } from "../l1/L1Button"
import styled from '@emotion/styled'
import { FusionColor } from '../../../theme/FusionStyle'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IconDefinition, faArrowProgress, faCheck, faCircleCheck, faCircleEllipsis, faCircleExclamation, faCircleXmark, faPaperPlane } from "@fortawesome/pro-solid-svg-icons"
import type * as CSS from 'csstype'
import { DiagnosisReviewAuditValidation } from '../../../redux/diagnosisReviewSlice'
import { s } from '@fusion-advantage/formlib'
import Loading from '../../Loading'

const AuditReviewTriggerWrapper = styled.div(({theme}) => ({
	display: 'flex',
	justifyContent: 'flex-start',
	alignItems: 'center',
	flexDirection: 'column',
	gap: 5,
}))

interface AuditTriggerRowProps {
	gap?: CSS.Property.Gap
	height?: CSS.Property.Height
}

const AuditTriggerRow = styled.div<AuditTriggerRowProps>(({theme, gap = theme.spacing(2), height = 'auto' }) => ({
	display: 'flex',
	justifyContent: 'flex-start',
	alignItems: 'center',
	flexDirection: 'row',
	width: '100%',
	gap: gap,
	height: height
}))

interface AuditTriggerColumnProps {
	backgroundColor?: string
	withBorder?: boolean
	justifyContent?: CSS.Property.JustifyContent
	alignItems?: CSS.Property.AlignItems
	flex?: CSS.Property.Flex
	padding?: CSS.Property.Padding
}

const AuditTriggerColumn = styled.div<AuditTriggerColumnProps>(({
	theme, 
	withBorder = true, 
	backgroundColor = theme.colors[FusionColor.White],
	justifyContent = 'center',
	alignItems = 'flex-start',
	flex = 1,
	padding = theme.spacing(1,2),
}) => ({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: justifyContent,
	alignItems: alignItems,
	borderRadius: withBorder ? theme.spacing(1) : 0,
	border: withBorder ? `1px solid ${theme.colors[FusionColor.Lines]}` : 'none',
	height: theme.spacing(8),
	padding: padding,
	backgroundColor: backgroundColor,
	fontSize: '14px',
	fontWeight: 600,
	gap: 5,
	flex: flex,
	color: theme.colors[FusionColor.InputLabel]
}))

interface AuditTriggerColumnStatusProps {
	fontColor: string,
}

const AuditTriggerColumnStatus = styled.div<AuditTriggerColumnStatusProps>(({theme, fontColor = theme.colors[FusionColor.InputLabel]}) => ({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'flex-start',
	alignItems: 'center',
	color: fontColor,
	fontSize: '16px',
	gap: theme.spacing(1),
}))

export interface L3DiagnosisAuditReviewTriggerProps extends DiagnosisReviewAuditValidation {
	onSubmit: () => void
	onSubmitToClinician: () => void
	onRunComboCodes: () => void
	loading: boolean
}

enum StatusLevel {
	SUCCESS = 'Success',
	WARNING = 'Warning',
	ERROR = 'Error',
	CRITERIA_NOT_MET = 'Criteria Not Met'
}

type StatusReportEntry = {
	message: string,
	level: StatusLevel
}

enum AuditReviewAction {
	SubmitQueriesToClinician,
	RunComboCodes,
	CompleteAudit,
}

type StatusReport = {
	decisions: StatusReportEntry,
	queries: StatusReportEntry,
	secondaryCoding: StatusReportEntry,
	availableAction: AuditReviewAction|null,
}

const L3DiagnosisAuditReviewTrigger = ({
	clinicalPending,
	decisionsPending,
	rejectCommentsPending,
	queriesPending,
	queryCommentsPending,
	secondaryCodesHasRun,
	secondaryCodesPending,
	onSubmit,
	onSubmitToClinician,
	onRunComboCodes,
	loading,
}: L3DiagnosisAuditReviewTriggerProps) => {
	const theme = useTheme()

	const calcStatus = () => {
		const statusReport: StatusReport = {
			decisions: {
				message: 'Incomplete',
				level: StatusLevel.ERROR
			},
			queries: {
				message: 'Decisions Incomplete',
				level: StatusLevel.CRITERIA_NOT_MET
			},
			secondaryCoding: {
				message: 'Not Run',
				level: StatusLevel.CRITERIA_NOT_MET
			},
			availableAction: null,
		}
		if(clinicalPending > 0) {
			statusReport.decisions.message = `${clinicalPending} Missing Clinical Info`
			return statusReport
		}
		if(decisionsPending > 0) {
			statusReport.decisions.message = `${decisionsPending} ${s('Decision', decisionsPending)} Pending`
			return statusReport
		}
		if(rejectCommentsPending > 0) {
			statusReport.decisions.message = `${rejectCommentsPending} Reject ${s('Comment', rejectCommentsPending)} Pending`
			statusReport.decisions.level = StatusLevel.ERROR
			return statusReport
		}
		statusReport.decisions.message = 'Complete'
		statusReport.decisions.level = StatusLevel.SUCCESS
		if(queryCommentsPending > 0) {
			statusReport.queries.message = `${queryCommentsPending} Query ${s('Comment', queryCommentsPending)} Pending`
			statusReport.queries.level = StatusLevel.ERROR
			return statusReport
		}
		if(queriesPending > 0) {
			statusReport.queries.message = `${queriesPending} ${s('Query', queriesPending)} Open`
			statusReport.queries.level = StatusLevel.WARNING
			statusReport.availableAction = AuditReviewAction.SubmitQueriesToClinician
			return statusReport
		}
		statusReport.queries.message = 'No Open Queries'
		statusReport.queries.level = StatusLevel.SUCCESS
		if(!secondaryCodesHasRun) {
			statusReport.secondaryCoding.message = 'Not Run'
			statusReport.secondaryCoding.level = StatusLevel.CRITERIA_NOT_MET
			statusReport.availableAction = AuditReviewAction.RunComboCodes
			return statusReport
		}
		if(secondaryCodesPending > 0) {
			statusReport.secondaryCoding.message = `${secondaryCodesPending} ${s('Error', secondaryCodesPending)} Found`
			statusReport.secondaryCoding.level = StatusLevel.ERROR
			return statusReport
		}
		statusReport.secondaryCoding.message = 'Complete'
		statusReport.secondaryCoding.level = StatusLevel.SUCCESS
		statusReport.availableAction = AuditReviewAction.CompleteAudit
		return statusReport
	}

	const getStatusLevelColor = (statusLevel: StatusLevel): FusionColor => {
		switch(statusLevel) {
			case StatusLevel.CRITERIA_NOT_MET:
				return FusionColor.MiniHeader
			case StatusLevel.ERROR:
				return FusionColor.StopRed
			case StatusLevel.WARNING:
				return FusionColor.OrangeMain
			default:
				return FusionColor.GoGreen
		}
	}

	const getStatusLevelIcon = (statusLevel: StatusLevel): IconDefinition => {
		switch(statusLevel) {
			case StatusLevel.CRITERIA_NOT_MET:
				return faCircleEllipsis
			case StatusLevel.ERROR:
				return faCircleXmark
			case StatusLevel.WARNING:
				return faCircleExclamation
			default:
				return faCircleCheck
		}
	}

	const statusReport = calcStatus()
	
	return (
		<>
			<AuditReviewTriggerWrapper>
				<AuditTriggerRow gap={theme.spacing(6)} height={theme.spacing(3)}>
					<AuditTriggerColumn padding={0} flex={3} withBorder={false}>Validation</AuditTriggerColumn>
					<AuditTriggerColumn padding={0} flex={1} withBorder={false}>Action</AuditTriggerColumn>
				</AuditTriggerRow>
				<AuditTriggerRow>
					<AuditTriggerColumn>
						Decisions
						<AuditTriggerColumnStatus fontColor={theme.colors[getStatusLevelColor(statusReport.decisions.level)]}>
							<FontAwesomeIcon icon={getStatusLevelIcon(statusReport.decisions.level)} />
							{statusReport.decisions.message}
						</AuditTriggerColumnStatus>
					</AuditTriggerColumn>
					<AuditTriggerColumn>
						Queries
						<AuditTriggerColumnStatus fontColor={theme.colors[getStatusLevelColor(statusReport.queries.level)]}>
							<FontAwesomeIcon icon={getStatusLevelIcon(statusReport.queries.level)} />
							{statusReport.queries.message}
						</AuditTriggerColumnStatus>
					</AuditTriggerColumn>
					<AuditTriggerColumn>
						Secondary Coding
						<AuditTriggerColumnStatus fontColor={theme.colors[getStatusLevelColor(statusReport.secondaryCoding.level)]}>
							<FontAwesomeIcon icon={getStatusLevelIcon(statusReport.secondaryCoding.level)} />
							{statusReport.secondaryCoding.message}
						</AuditTriggerColumnStatus>
					</AuditTriggerColumn>
					<AuditTriggerColumn backgroundColor={theme.colors[FusionColor.Second]} alignItems='center'>
						{ loading ? (<Loading />) : null}
						{
							!loading && statusReport.availableAction === AuditReviewAction.SubmitQueriesToClinician ? (
								<L1ReasonableButton
									color={FusionColor.BlueMain}
									icon={faPaperPlane}
									expand
									label='Submit Queries to Clinician'
									onClick={onSubmitToClinician}
									wide={true}
								/>
							) : null
						}
						{
							!loading && statusReport.availableAction === AuditReviewAction.RunComboCodes ? (
								<L1ReasonableButton
									color={FusionColor.BlueMain}
									icon={faArrowProgress}
									expand
									label='Run Combo Codes'
									onClick={onRunComboCodes}
									wide={true}
								/>
							) : null
						}
						{
							!loading && statusReport.availableAction === AuditReviewAction.CompleteAudit ? (
								<L1ReasonableButton
									icon={faCheck}
									expand
									label='Complete Audit'
									onClick={onSubmit}
									wide={true}
								/>
							) : null
						}
					</AuditTriggerColumn>
				</AuditTriggerRow>
			</AuditReviewTriggerWrapper>
		</>
	)
}

export default L3DiagnosisAuditReviewTrigger